import type {
  ClientToServerWebsocketCallbackData,
  ServerToClientWebsocketBroadcastData,
} from "schemas/generated/websocket";

import React, { createContext, useEffect, useRef, useState } from "react";

import useCurrentUser from "@editor/hooks/useCurrentUser";

import { useRequiredContext } from "replo-utils/react/context";

import { WebSocketManager } from "../utils/webSocketManager";

interface ProjectWebsocketContextType {
  subscribe: (
    callback: (data: ServerToClientWebsocketBroadcastData) => void,
  ) => string;
  unsubscribe: (subscriptionId: string) => void;
  publish: (update: ClientToServerWebsocketCallbackData) => void;
}

const ProjectWebsocketContext =
  createContext<ProjectWebsocketContextType | null>(null);

interface ProjectWebsocketProviderProps {
  projectId: string;
  children: React.ReactNode;
}

export function ProjectWebsocketProvider({
  projectId,
  children,
}: ProjectWebsocketProviderProps) {
  // Map of subscriptionId to callback
  const subscribersRef = useRef(
    new Map<string, (data: ServerToClientWebsocketBroadcastData) => void>(),
  );

  const { user } = useCurrentUser();
  const jwt = user?.jwt ?? "";

  useEffect(() => {
    // Subscribe all current subscribers to the new project on id change
    const oldSubscribers = new Map(subscribersRef.current);
    subscribersRef.current.clear();

    oldSubscribers.forEach((callback) => {
      const subscriptionId = WebSocketManager.subscribe(
        projectId,
        jwt,
        callback,
      );
      subscribersRef.current.set(subscriptionId, callback);
    });

    // Store current subscribers for cleanup
    const currentSubscribers = subscribersRef.current;

    // Unsubscribe all subscribers when unmounting or projectId changes
    return () => {
      currentSubscribers.forEach((_, subscriptionId) => {
        WebSocketManager.unsubscribe(projectId, subscriptionId);
      });
      currentSubscribers.clear();
    };
  }, [projectId, jwt]);

  const contextValue = React.useMemo(
    () => ({
      subscribe: (
        callback: (data: ServerToClientWebsocketBroadcastData) => void,
      ) => {
        const subscriptionId = WebSocketManager.subscribe(
          projectId,
          jwt,
          callback,
        );
        subscribersRef.current.set(subscriptionId, callback);
        return subscriptionId;
      },
      unsubscribe: (subscriptionId: string) => {
        if (subscribersRef.current.has(subscriptionId)) {
          WebSocketManager.unsubscribe(projectId, subscriptionId);
          subscribersRef.current.delete(subscriptionId);
        }
      },
      publish: (data: ClientToServerWebsocketCallbackData) => {
        WebSocketManager.publishUpdate(projectId, data);
      },
    }),
    [projectId, jwt],
  );

  return (
    <ProjectWebsocketContext.Provider value={contextValue}>
      {children}
    </ProjectWebsocketContext.Provider>
  );
}

const PRESENCE_COLORS = [
  "#6CB594",
  "#FFB931",
  "#E02F66",
  "#73C6E9",
  "#FFBEC6",
  "#274AE2",
];

function getUserColor(userId: string) {
  // Simple stable mapping: sum of character codes modulo array length
  const charCodeSum = userId
    .split("")
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return PRESENCE_COLORS[charCodeSum % PRESENCE_COLORS.length];
}

export function useProjectPresences({
  pageId,
  funnelId,
}: { pageId?: string; funnelId?: string } = {}) {
  const context = useRequiredContext(ProjectWebsocketContext);
  const subscriptionIdRef = useRef<string>();
  const [presenceData, setPresenceData] = useState<{
    page: Record<
      string,
      { userId: string; userName: string; isSuperuser: boolean }[]
    >;
    funnel: Record<
      string,
      { userId: string; userName: string; isSuperuser: boolean }[]
    >;
    all: { userId: string; userName: string; isSuperuser: boolean }[];
  }>({
    page: {},
    funnel: {},
    all: [],
  });
  const { user } = useCurrentUser();

  useEffect(() => {
    const onUpdate = (data: ServerToClientWebsocketBroadcastData) => {
      if (data.type === "PRESENCE_UPDATE" && data.presenceData) {
        setPresenceData({
          page: data.presenceData.pagePresence,
          funnel: data.presenceData.funnelPresence,
          all: data.presenceData.projectPresence,
        });
      }
    };

    // Always subscribe when the context changes
    subscriptionIdRef.current = context.subscribe(onUpdate);

    // Send initial presence update when mounting
    if (user) {
      const data: ClientToServerWebsocketCallbackData = {
        type: "PRESENCE_UPDATE",
        presenceData: {
          userId: user.id,
          userName: user.name ?? "Anonymous",
          pageId,
          funnelId,
          isSuperuser: user.isSuperuser ?? false,
        },
      };
      context.publish(data);
    }

    return () => {
      // Clean up subscription when unmounting or when context changes
      if (subscriptionIdRef.current) {
        context.unsubscribe(subscriptionIdRef.current);
      }
    };
  }, [context, pageId, funnelId, user]);

  let relevantPresences: {
    userId: string;
    userName: string;
    isSuperuser: boolean;
  }[] = [];
  if (funnelId) {
    relevantPresences = presenceData.funnel[funnelId] ?? [];
  } else if (pageId) {
    relevantPresences = presenceData.page[pageId] ?? [];
  } else {
    relevantPresences = presenceData.all;
  }

  return relevantPresences
    .filter((presence) => presence.userId !== user?.id)
    .map((presence) => {
      return {
        ...presence,
        color: getUserColor(presence.userId),
      };
    });
}

export function useProjectContentUpdate(callback: () => void) {
  const context = useRequiredContext(ProjectWebsocketContext);
  const subscriptionIdRef = useRef<string>();

  useEffect(() => {
    const onUpdate = (data: ServerToClientWebsocketBroadcastData) => {
      if (data.type === "CONTENT_UPDATE") {
        callback();
      }
    };

    // Always subscribe when the context changes
    subscriptionIdRef.current = context.subscribe(onUpdate);

    return () => {
      // Clean up subscription when unmounting or when context changes
      if (subscriptionIdRef.current) {
        context.unsubscribe(subscriptionIdRef.current);
      }
    };
  }, [context, callback]);
}
